// ─── Alert component ───────────────────────────────────────────────────────────

import React, { ReactNode } from 'react';
import { cn } from '../../utils/helpers';
import type { AlertVariant } from '../../types';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  icon?: ReactNode;
}

const variantClasses: Record<AlertVariant, string> = {
  success: 'bg-successLight border-success/30 text-success',
  error:   'bg-errorLight border-error/30 text-error',
  warning: 'bg-warningLight border-warning/30 text-warning',
  info:    'bg-infoLight border-info/30 text-info',
};

const defaultIcons: Record<AlertVariant, ReactNode> = {
  success: (
    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
};

const Alert: React.FC<AlertProps> = ({ variant = 'info', title, children, className, onClose, icon }) => (
  <div
    role="alert"
    className={cn(
      'flex items-start gap-3 rounded-lg border p-3.5 text-[13px]',
      variantClasses[variant],
      className
    )}
  >
    <span className="mt-0.5">{icon ?? defaultIcons[variant]}</span>
    <div className="flex-1 min-w-0">
      {title && <p className="font-semibold text-[13px] mb-0.5">{title}</p>}
      <div className="text-[13px] opacity-90 leading-relaxed">{children}</div>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        aria-label="Dismiss"
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    )}
  </div>
);

export default Alert;
